// The tenant colour palette: preset colours managed on the admin Design tab
// and offered as picker presets everywhere a colour can be chosen. Stored
// inside design_tokens as `palette-1` … `palette-N` entries, so it needs no
// schema change, survives the existing validation pipeline, and is exposed to
// the published site as `--palette-n` custom properties for free.

export const MAX_PALETTE_COLORS = 12;

// The first two slots are mandatory and have fixed meanings: slot 1 is the
// Primary Brand Colour (feeds `accent` unless overridden) and slot 2 is the
// Base Background Colour (feeds `bg`). Remaining slots are free extras.
export const PRIMARY_BRAND_SLOT = 0;
export const BASE_BACKGROUND_SLOT = 1;
export const MANDATORY_PALETTE_SLOTS = 2;

/** Token key holding the calculated readable-text colour for palette slot i. */
export function paletteTextKey(index: number): string {
  return `palette-${index + 1}-text`;
}

const PALETTE_KEY_RE = /^palette-(\d+)$/;
const PALETTE_TEXT_KEY_RE = /^palette-(\d+)-text$/;

/** True for `palette-N-text` keys (calculated, never edited directly). */
export function isPaletteTextKey(key: string): boolean {
  return PALETTE_TEXT_KEY_RE.test(key);
}

/** Parse a config.design_tokens value (JSON string or object) to a record. */
export function parseDesignTokens(raw: unknown): Record<string, string> {
  if (!raw) return {};
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  const tokens: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value === "string") tokens[key] = value;
  }
  return tokens;
}

/** The palette colours in slot order, read from a design-tokens record. */
export function paletteFromTokens(tokens: Record<string, string>): string[] {
  return Object.entries(tokens)
    .map(([key, value]) => {
      const match = PALETTE_KEY_RE.exec(key);
      return match ? { slot: Number(match[1]), value } : null;
    })
    .filter((entry): entry is { slot: number; value: string } => entry !== null)
    .sort((a, b) => a.slot - b.slot)
    .map((entry) => entry.value)
    .slice(0, MAX_PALETTE_COLORS);
}

/** Re-key a tokens record with the given palette in slots 1…N (old slots and
 * their calculated `palette-N-text` companions cleared). */
export function tokensWithPalette(
  tokens: Record<string, string>,
  palette: string[]
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const [key, value] of Object.entries(tokens)) {
    if (!PALETTE_KEY_RE.test(key) && !PALETTE_TEXT_KEY_RE.test(key)) next[key] = value;
  }
  palette.slice(0, MAX_PALETTE_COLORS).forEach((color, index) => {
    if (color.trim()) next[`palette-${index + 1}`] = color.trim();
  });
  return next;
}
